<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import { sessionHelper, ddlHelper } from "@/utils";
import { SESSION, SHARED } from "@/constants";
import { required, integer, minValue } from "vuelidate/lib/validators";
import { mapState } from "vuex";
import i18n from "@/i18n";
import { DOMAIN_MODULE } from '@/state/modules/domain'
import { DOMAIN_GROUP_MODULE } from '@/state/modules/domainGroup'
import { domainMethods, domainGroupMethods } from "@/state/helpers";
import Multiselect from "vue-multiselect";

const hostname = (value) => {
  if (value == null || value == '') {
    return true;
  }
  return /^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?)+$/.test(value);
};

export default {
  page: {
    title: i18n.t("label.updateFormX", [i18n.t("module.domain")]),
  },
  components: { Layout, PageHeader, Multiselect },
  beforeRouteLeave(to, from, next) {
    if (to.name != "Domain-listing") {
      sessionHelper.clearSearch(SESSION.DOMAIN_SEARCH);
    }
    next();
  },
  computed: {
    ...mapState(DOMAIN_MODULE, ["status"]),
    ...mapState(DOMAIN_GROUP_MODULE, { groupList: (state) => state.list.data }),
    groupDdl() {
      // A group is one brand in one market: only groups of the domain's own
      // country can take it.
      return this.groupList
        .filter((g) => !this.data.countryCode || g.countryCode === this.data.countryCode)
        .map((g) => {
          return { value: g.id, text: g.slug ? g.name + ' (' + g.slug + ')' : g.name };
        });
    },
    selectedGroup: {
      get() {
        return this.groupDdl.find((g) => g.value === this.data.domainGroupUuid) || null;
      },
      set(option) {
        this.data.domainGroupUuid = option ? option.value : null;
      },
    },
    data() {
      return this.$store.state.domain.details;
    },
    selectedCountryCode: {
      get() {
        return this.countryCodeDdl.find((c) => c.value === this.data.countryCode) || null;
      },
      set(option) {
        this.data.countryCode = option ? option.value : null;
      },
    },
  },
  data() {
    return {
      title: i18n.t("label.updateFormX", [i18n.t("module.domain")]),
      items: [
        {
          text: i18n.t("module.dashboard"),
          href: "/",
        },
        {
          text: i18n.t("module.domain"),
          href: "/domain",
        },
        {
          text: i18n.t("label.updateX", [i18n.t("module.domain")]),
          active: true,
        },
      ],
      submitted: false,
      permissions: sessionHelper.getPermissions(),
      updatePermission: null,
      readPermission: null,
      // Only the markets this user may see.
      countryCodeDdl: ddlHelper.getMonitorCountries(),
    };
  },
  validations: {
    data: {
      domain: { required, hostname },
      countryCode: { required },
    },
  },
  async created() {
    this.searchCriteria = sessionHelper.retrieveSearch(SESSION.DOMAIN_SEARCH);
    await this.validPermissions();
    this.loadGroups();
    this.getDomainDetails();
  },
  methods: {
    ...domainMethods,
    ...domainGroupMethods,
    loadGroups() {
      // Groups belong to the current user; the backend scopes the list.
      this.getAllDomainGroups({
        filter: {
          pageNumber: SHARED.DEFAULT_PAGE_NUMBER,
          pageSize: 100,
          isActive: SHARED.DEFAULT_ALL_VALUE,
          searchArray: [],
        },
      });
    },
    async validPermissions() {
      var jsonPermissions = JSON.parse(this.permissions)
      if (jsonPermissions.some(p => p.code === "DOMAIN_UPDATE")) {
        this.updatePermission = true;
      }
      else {
        this.updatePermission = false;
      }

      if (jsonPermissions.some(p => p.code === "DOMAIN_READ")) {
        this.readPermission = true;
      }
      else {
        this.readPermission = false;
      }
    },
    getDomainDetails() {
      if (this.readPermission == true) {
        this.getDomain({ uuid: this.searchCriteria.selectedRecordId, });
      }
    },
    normalizeDomain() {
      let value = (this.data.domain || '').trim().toLowerCase();
      value = value.replace(/^[a-z]+:\/\//, '');
      value = value.split('/')[0];
      this.data.domain = value;
    },
    requestUpdate() {
      this.normalizeDomain();
      this.submitted = true;
      // stop here if form is invalid
      this.$v.$touch();

      if (this.$v.$invalid) {
        return;
      } else {
        let data = {
          id: this.data.id,
          domain: this.data.domain,
          countryCode: this.data.countryCode,
          remark: this.data.remark,
          domainGroupUuid: this.data.domainGroupUuid,
        };
        if (this.updatePermission == true) {
          this.updateDomain({ data });
        }
      }
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <div class="row">
      <div class="col">
        <div class="card">
          <div class="card-body">
            <form @submit.prevent="requestUpdate" v-if="readPermission == true">
              <b-form-group id="domain" label-cols-sm="2" label-cols-lg="2" :label="$t('label.domain')"
                label-for="domain">
                <b-form-input for="domain" v-model.trim="data.domain" @change="normalizeDomain" :class="{
                  'is-invalid': submitted &&
                    (!$v.data.domain.required || !$v.data.domain.hostname)
                }"></b-form-input>
                <div v-if="submitted && (!$v.data.domain.required || !$v.data.domain.hostname)"
                  class="invalid-feedback">
                  <span v-if="!$v.data.domain.required">{{
                    $t("message.requiredX", [$t("label.domain")])
                  }}</span>
                  <span v-if="!$v.data.domain.hostname">{{
                    $t("message.validX", [$t("label.domain")])
                  }}</span>
                </div>
              </b-form-group>

              <b-form-group id="countryCode" label-cols-sm="2" label-cols-lg="2" :label="$t('label.countryCode')"
                label-for="countryCode">
                <multiselect v-model="selectedCountryCode" :options="countryCodeDdl" label="text" track-by="value"
                  :show-labels="false" :allow-empty="false"
                  :placeholder="$t('message.pleaseSelectX', [$t('label.countryCode')])"></multiselect>
                <div v-if="submitted && !$v.data.countryCode.required" class="invalid-feedback">
                  <span v-if="!$v.data.countryCode.required">{{
                    $t("message.requiredX", [$t("label.countryCode")])
                  }}</span>
                </div>
              </b-form-group>

              <b-form-group id="group" label-cols-sm="2" label-cols-lg="2" :label="$t('label.group')"
                label-for="group">
                <multiselect v-model="selectedGroup" :options="groupDdl" label="text" track-by="value"
                  :show-labels="false" :allow-empty="true"
                  :placeholder="$t('message.pleaseSelectX', [$t('label.group')])"></multiselect>
                <small class="form-text text-muted">{{ $t('message.domainGroupHelp') }}</small>
              </b-form-group>


              <b-form-group id="remark" label-cols-sm="2" label-cols-lg="2" :label="$t('label.remark')"
                label-for="remark">
                <b-form-textarea for="remark" v-model.trim="data.remark" rows="3"></b-form-textarea>
              </b-form-group>

              <div class="mt-4 text-center" style="float: right">
                <button class="btn btn-info w-md waves-effect waves-light" style="margin-right: 10px" type="submit"
                  :disabled="status.processing || updatePermission != true">
                  {{ $t("label.updateX", [$t("module.domain")]) }}
                </button>
              </div>
            </form>
            <div style="text-align: center;" v-else>
              {{ $t('message.noPermissionMessage') }}
            </div>
          </div>
        </div>
        <!-- end card -->
      </div>
      <!-- end col -->
    </div>
  </Layout>
</template>
